import React from 'react';
import './ProductCard.scss';
import { getSourceHref } from '../../utils/urls';

interface ProductCardProps {
  title?: string;
  imageUrl?: string | null;
  domain?: string | null;
  url?: string | null;
  priceRaw?: string | null;
  priceValue?: number | null;
  priceCurrency?: string | null;
}

// Rounds a numeric price to at most 2 decimals (money precision). Raw floats printed verbatim keep
// their FP tail (e.g. 19.9899999), so every numeric fallback goes through here — the currency branch
// below already rounds via Intl.
const formatAmount = (value: number): string =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value);

// Only the GPT channel captures a display string (priceRaw); AI Mode / Copilot deliver a numeric
// value + ISO-ish currency — format those so their cards don't render priceless.
const formatPrice = (
  priceRaw?: string | null,
  priceValue?: number | null,
  priceCurrency?: string | null
): string | null => {
  if (priceRaw) return priceRaw;
  if (priceValue == null || !Number.isFinite(priceValue)) return null;
  if (priceCurrency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: priceCurrency }).format(
        priceValue
      );
    } catch {
      return `${formatAmount(priceValue)} ${priceCurrency}`;
    }
  }
  return formatAmount(priceValue);
};

/**
 * Presentational product card (image + title + domain link + price). Shared by the prompt-view V2
 * "Shopping Cards" answer panel and the bottom "Product Cards" strip. Callers size the card via the
 * list container (e.g. `.product-cards-section__list .product-card`).
 */
export const ProductCard: React.FC<ProductCardProps> = ({
  title,
  imageUrl,
  domain,
  url,
  priceRaw,
  priceValue,
  priceCurrency,
}) => {
  const displayDomain = domain ?? url ?? '';
  const displayPrice = formatPrice(priceRaw, priceValue, priceCurrency);
  // Prefer the real product url. Some scraper items arrive without one (GPT shopping occasionally
  // returns `link: null` and there is no usable url anywhere in the payload), so fall back to a web
  // search for the product title — keeping every card clickable. Resolved at render time, so it also
  // covers already-stored linkless cards without a backfill. Only the title uses the fallback; the
  // domain label stays hidden (displayDomain is empty when there is no real url).
  const href = url
    ? getSourceHref(url)
    : title
    ? `https://www.google.com/search?q=${encodeURIComponent(title)}`
    : null;

  return (
    <div className="product-card">
      <div className="product-card__image-box">
        {/* Fallback icon sits underneath; the img covers it and reveals it on load error. */}
        <span className="ico--shopping-cart ico--sm product-card__image-fallback" />
        {imageUrl && (
          <img
            className="product-card__image"
            src={imageUrl}
            alt=""
            loading="lazy"
            // Many shop/Google CDNs reject hotlinked requests that carry a referrer.
            referrerPolicy="no-referrer"
            onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
          />
        )}
      </div>
      <div className="product-card__body">
        {href ? (
          <a
            className="product-card__title"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
          >
            {title}
          </a>
        ) : (
          <span className="product-card__title">{title}</span>
        )}
        {displayDomain &&
          (href ? (
            <a
              className="product-card__domain"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {displayDomain}
            </a>
          ) : (
            <span className="product-card__domain">{displayDomain}</span>
          ))}
        {displayPrice && <span className="product-card__price">{displayPrice}</span>}
      </div>
    </div>
  );
};

export default ProductCard;
